import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import { userIsOwner } from '../config/ownerAccess';
import { graphRequest } from '../msalConfig';
import {
  loadDirectory, matchesQuery, telHref, reloadPerson, fetchHeld, resolvePerson,
  savePerson, createPerson, nextPersonId,
  saveChannel, addChannel, deleteChannel, setPrimary, clearPrimary,
  findNormalizedOwners, loadPhotoMap, galleryNames, initialsOf,
  uploadPersonPhoto, setPhotoRef,
  reconcilePerson, deletePerson,
} from './contactsApi';
import DeletePerson, { removePerson } from './DeletePerson';
import { createReconcileQueue, pillFor, stateFromResult, stateFromColumn, stateFromResolve } from './reconcileQueue';
import { BUTTONS, panelOpen, panelLoaded, panelFailed, panelBusy, panelDone, resolveErrorText } from './resolvePanel';
import {
  normalizeValue, isValidValue, formatPhoneForDisplay,
  isValidBirthday, formatBirthday,
} from './normalize';
import './Contacts.css';

// ============================================================
// CONTACTS v1 — directory with editing.
//
// Two rules run through the whole file:
//
//   1. NOTHING IS SHOWN AS SAVED UNTIL SHAREPOINT SAYS IT IS. Every mutation
//      re-reads that person and their channels and re-renders from the result.
//      Local form state is never promoted to "saved" — if SharePoint coerced,
//      truncated or rejected part of a write, the re-read is what reveals it.
//   2. Absent is not false. IsPrimary and StandingSender are tested === true,
//      because 286 of 1,909 live channel rows carry no IsPrimary key at all.
//
// Every field is optional in the data (the importer omits blanks rather than
// storing ""), so nothing may render a value without checking it first.
// ============================================================

const PAGE = 100;          // rows rendered per chunk — see RENDER note in Contacts.css
const KINDS = ['Person', 'Business'];
const DOMAINS = ['', 'FIG', 'Family'];

const displayValue = c =>
  c.kind === 'Phone' ? formatPhoneForDisplay(c.value) : c.value;

/**
 * Round avatar, or initials when there is no photo.
 * `photo` is resolved from PhotoRef at render time — no Graph URL is ever
 * stored, because they expire within about an hour.
 */
function Avatar({ person, photo, size = 'sm', onOpen }) {
  const [failed, setFailed] = useState(false);
  const url = size === 'lg' ? (photo?.thumbLarge || photo?.fullUrl) : photo?.thumbSmall;
  const cls = `Contacts-avatar Contacts-avatar--${size}`;
  if (!url || failed) {
    return <span className={`${cls} Contacts-avatar--initials`} aria-hidden="true">{initialsOf(person)}</span>;
  }
  return (
    <img
      className={cls}
      src={url}
      alt=""
      loading="lazy"
      onError={() => setFailed(true)}
      onClick={onOpen ? e => { e.stopPropagation(); onOpen(); } : undefined}
      style={onOpen ? { cursor: 'zoom-in' } : undefined}
    />
  );
}

// ── one phone/email line ─────────────────────────────────────────────────────

// Sync A pill — the state of this person's PHONE copy, separate from Row.err
// (which is about the SharePoint save). Rendered inside the row-head button, so
// it is a span that stops propagation rather than a nested button. Tap reruns
// the reconcile where a rerun makes sense.
function SyncPill({ state, onRetry, onResolve }) {
  const pill = pillFor(state);
  if (!pill) return null;
  // Viewers get no handlers, so the pill is informational only for them.
  const tappable = (pill.retry && !!onRetry) || (pill.resolve && !!onResolve);
  const tap = e => { e.stopPropagation(); if (pill.retry && onRetry) onRetry(); else if (pill.resolve && onResolve) onResolve(); };
  return (
    <span className={`Contacts-sync Contacts-sync--${pill.tone}${tappable ? ' Contacts-sync--tap' : ''}`}
      role={tappable ? 'button' : undefined} tabIndex={tappable ? 0 : undefined}
      onClick={tap} onKeyDown={e => { if (tappable && (e.key === 'Enter' || e.key === ' ')) tap(e); }}
      title={pill.retry ? 'Tap to retry' : pill.resolve ? 'Tap to resolve' : undefined}>
      {pill.text}
    </span>
  );
}

// The drop-down under a held / deleted-on-phone pill (resolvePanel.js drives
// the state). One line per differing field, then the choice buttons. The
// phone/app values shown here come from contacts-held and are rendered only.
function ResolvePanel({ panel, onChoose, onClose }) {
  if (!panel) return null;
  const buttons = BUTTONS[panel.kind] || [];
  return (
    <div className="Contacts-resolve" role="group" aria-label="Resolve phone copy" onClick={e => e.stopPropagation()}>
      {panel.kind === 'held' && (panel.loading
        ? <p className="Contacts-resolve-note">Comparing with the phone…</p>
        : panel.fields.length === 0 && !panel.error
          ? <p className="Contacts-resolve-note">No field differs any more — pick a side to clear the hold.</p>
          : panel.fields.map(f => (
            <div className="Contacts-resolve-row" key={f.key}>
              <span className="Contacts-resolve-field">{f.field}</span>
              <span className="Contacts-resolve-vals">
                <span><b>Phone:</b> {f.phone || <i>blank</i>}</span>
                <span><b>App:</b> {f.app || <i>blank</i>}</span>
              </span>
            </div>
          )))}
      {panel.kind === 'phone-deleted' && (
        <p className="Contacts-resolve-note">This contact was deleted on the phone. The app still has it.</p>
      )}
      {panel.error && <p className="Contacts-inline-error">{panel.error}</p>}
      <span className="Contacts-confirm-actions">
        {buttons.map(b => (
          <button type="button" key={b.choice} className="Contacts-btn Contacts-btn--primary" disabled={!!panel.busy || panel.loading}
            onClick={() => onChoose(b.choice)}>{panel.busy === b.choice ? 'Working…' : b.label}</button>
        ))}
        <button type="button" className="Contacts-btn" disabled={!!panel.busy} onClick={onClose}>Close</button>
      </span>
    </div>
  );
}

function ChannelRow({ chan, editing, canEdit, busy, onStar, onEdit, onDelete }) {
  const href = chan.kind === 'Phone' ? telHref(chan.value) : `mailto:${chan.value}`;
  return (
    <div className="Contacts-chan">
      {canEdit
        ? <button
            type="button"
            className={`Contacts-star${chan.isPrimary ? ' Contacts-star--on' : ''}`}
            title={chan.isPrimary ? 'Primary — tap to clear' : 'Make primary'}
            aria-label={chan.isPrimary ? 'Primary, tap to clear' : 'Make primary'}
            disabled={busy}
            onClick={e => { e.stopPropagation(); onStar(chan); }}
          >{chan.isPrimary ? '★' : '☆'}</button>
        // Viewer: the same column, no control. Primary is still shown.
        : <span className={`Contacts-star${chan.isPrimary ? ' Contacts-star--on' : ''}`} aria-label={chan.isPrimary ? 'Primary' : undefined} aria-hidden={chan.isPrimary ? undefined : 'true'}>{chan.isPrimary ? '★' : ''}</span>}

      {/* Always rendered, even when empty, so values stay in one column. */}
      <span className="Contacts-chan-label">{chan.label}</span>

      {href
        ? <a className="Contacts-chan-value" href={href} onClick={e => e.stopPropagation()}>{displayValue(chan)}</a>
        : <span className="Contacts-chan-value">{displayValue(chan)}</span>}

      {editing && canEdit && (
        <span className="Contacts-chan-actions">
          <button type="button" className="Contacts-mini" disabled={busy}
            onClick={e => { e.stopPropagation(); onEdit(chan); }}>Edit</button>
          <button type="button" className="Contacts-mini Contacts-mini--danger" disabled={busy}
            onClick={e => { e.stopPropagation(); onDelete(chan); }}>Delete</button>
        </span>
      )}
    </div>
  );
}

// ── person edit form ─────────────────────────────────────────────────────────

function PersonForm({ person, busy, onCancel, onSave }) {
  const [f, setF] = useState(() => ({
    Title: person?.title || '',
    GivenName: person?.givenName || '',
    FamilyName: person?.familyName || '',
    Kind: person?.kind || 'Person',
    Org: person?.org || '',
    JobTitle: person?.jobTitle || '',
    Domain: person?.domain || '',
    Relationship: person?.relationship || '',
    Context: person?.context || '',
    Tags: person?.tags || '',
    Birthday: person?.birthday || '',
    StandingSender: person?.standingSender === true,
    Notes: person?.notes || '',
  }));
  const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));
  const bdayBad = !isValidBirthday(f.Birthday);
  const nameBad = !f.Title.trim();

  return (
    <form
      className="Contacts-form"
      onSubmit={e => { e.preventDefault(); if (!bdayBad && !nameBad) onSave(f); }}
    >
      <label className="Contacts-field Contacts-field--wide">
        <span>Name</span>
        <input value={f.Title} onChange={e => set('Title', e.target.value)} autoFocus />
        {nameBad && <em className="Contacts-hint Contacts-hint--bad">A name is required</em>}
      </label>

      <label className="Contacts-field"><span>First</span>
        <input value={f.GivenName} onChange={e => set('GivenName', e.target.value)} /></label>
      <label className="Contacts-field"><span>Last</span>
        <input value={f.FamilyName} onChange={e => set('FamilyName', e.target.value)} /></label>

      <label className="Contacts-field"><span>Kind</span>
        <select value={f.Kind} onChange={e => set('Kind', e.target.value)}>
          {KINDS.map(k => <option key={k} value={k}>{k}</option>)}
        </select></label>

      <label className="Contacts-field"><span>Domain</span>
        <select value={f.Domain} onChange={e => set('Domain', e.target.value)}>
          {DOMAINS.map(d => <option key={d || 'blank'} value={d}>{d || '—'}</option>)}
        </select></label>

      <label className="Contacts-field"><span>Company</span>
        <input value={f.Org} onChange={e => set('Org', e.target.value)} /></label>
      <label className="Contacts-field"><span>Job title</span>
        <input value={f.JobTitle} onChange={e => set('JobTitle', e.target.value)} /></label>
      <label className="Contacts-field"><span>Relationship</span>
        <input value={f.Relationship} onChange={e => set('Relationship', e.target.value)} /></label>
      <label className="Contacts-field"><span>Tags</span>
        <input value={f.Tags} onChange={e => set('Tags', e.target.value)}
          placeholder="pipe|delimited" /></label>

      <label className="Contacts-field"><span>Birthday</span>
        <input value={f.Birthday} onChange={e => set('Birthday', e.target.value)}
          placeholder="YYYY-MM-DD or --MM-DD" />
        {bdayBad
          ? <em className="Contacts-hint Contacts-hint--bad">Use YYYY-MM-DD or --MM-DD</em>
          : f.Birthday
            ? <em className="Contacts-hint">{formatBirthday(f.Birthday)}</em>
            : null}
      </label>

      <label className="Contacts-field Contacts-field--wide"><span>Context</span>
        <textarea rows={2} value={f.Context} onChange={e => set('Context', e.target.value)} /></label>
      <label className="Contacts-field Contacts-field--wide"><span>Notes</span>
        <textarea rows={4} value={f.Notes} onChange={e => set('Notes', e.target.value)} /></label>

      <label className="Contacts-toggle Contacts-field--wide">
        <input type="checkbox" checked={f.StandingSender}
          onChange={e => set('StandingSender', e.target.checked)} />
        <span>Always recognize in Watch (not wired yet)</span>
      </label>

      <div className="Contacts-form-actions Contacts-field--wide">
        <button type="submit" className="Contacts-btn Contacts-btn--primary"
          disabled={busy || bdayBad || nameBad}>{busy ? 'Saving…' : 'Save'}</button>
        <button type="button" className="Contacts-btn" disabled={busy} onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}

// ── add / edit a channel ─────────────────────────────────────────────────────

function ChannelForm({ people, person, channel, busy, onCancel, onSave }) {
  const isNew = !channel;
  const [kind, setKind] = useState(channel?.kind || 'Phone');
  const [value, setValue] = useState(channel?.value || '');
  const [label, setLabel] = useState(channel?.label || '');

  const normalized = normalizeValue(kind, value);
  const valid = isValidValue(kind, value);
  const owners = findNormalizedOwners(people, kind, normalized, channel?.id);
  const onThisPerson = owners.filter(o => o.personId === person?.personId);
  const onOthers = owners.filter(o => o.personId !== person?.personId);
  const blocked = !valid || onThisPerson.length > 0;

  return (
    <form className="Contacts-chanform"
      onSubmit={e => { e.preventDefault(); if (!blocked) onSave({ kind, value, label }); }}>
      <div className="Contacts-chanform-row">
        {isNew ? (
          <select value={kind} onChange={e => setKind(e.target.value)} aria-label="Kind">
            <option value="Phone">Phone</option>
            <option value="Email">Email</option>
          </select>
        ) : <span className="Contacts-chan-label">{kind}</span>}
        <input value={value} onChange={e => setValue(e.target.value)}
          placeholder={kind === 'Phone' ? 'Phone number' : 'name@example.com'} autoFocus />
        <input className="Contacts-chanform-label" value={label}
          onChange={e => setLabel(e.target.value)} placeholder="Label" />
      </div>

      {value && !valid && (
        <p className="Contacts-hint Contacts-hint--bad">
          {kind === 'Phone' ? 'Needs at least 7 digits.' : 'Needs a name@domain.tld address.'}
        </p>
      )}
      {onThisPerson.length > 0 && (
        <p className="Contacts-hint Contacts-hint--bad">
          This person already has that {kind.toLowerCase()}.
        </p>
      )}
      {onThisPerson.length === 0 && onOthers.length > 0 && valid && (
        <p className="Contacts-hint Contacts-hint--warn">
          Also on {onOthers.map(o => o.title || o.personId).join(', ')}. Households share numbers — saving is allowed.
        </p>
      )}

      <div className="Contacts-form-actions">
        <button type="submit" className="Contacts-btn Contacts-btn--primary" disabled={busy || blocked}>
          {busy ? 'Saving…' : isNew ? 'Add' : 'Save'}
        </button>
        <button type="button" className="Contacts-btn" disabled={busy} onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}

// ── photos ───────────────────────────────────────────────────────────────────
// Main = PhotoRef, never a file name; the gallery is every library file named
// for this PersonId (galleryNames). Bytes go browser -> library only; the phone
// copy is the App Service's job, kicked by the same reconcile a save fires.
// Every action here runs through onAct -> Row.run -> page.mutate with
// { photos: true }, so the thumbnail map (whose URLs expire) is rebuilt after.
function PhotoSection({ person, photoMap, busy, canEdit, onLightbox, onAct }) {
  // null | 'remove' (main file present) | 'clear' (PhotoRef names a missing file). Same confirm, same write.
  const [pendingClear, setPendingClear] = useState(null);
  const names = galleryNames(photoMap, person.personId);
  const main = person.photoRef;
  const hasMainFile = !!main && names.includes(main);
  const ordered = hasMainFile ? [main, ...names.filter(n => n !== main)] : names;

  const pick = e => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';                       // same file again must re-fire onChange
    if (!file) return;
    onAct(async t => {
      const { name } = await uploadPersonPhoto(t, person.personId, file, names);
      try {
        return await setPhotoRef(t, person, name);
      } catch (err) {
        // The file is in the library but not main. Say so; "Make main" fixes it.
        throw new Error(`Uploaded as ${name}, but it could not be made the main photo (${err.message || 'save failed'}). Tap "Make main" on it.`);
      }
    });
  };

  return (
    <div className="Contacts-photosec">
      <div className="Contacts-photos">
        {ordered.map(n => {
          const p = photoMap.get(n);
          const isMain = n === main;
          const src = p?.thumbLarge || p?.thumbSmall || p?.fullUrl || '';
          return (
            <figure key={n} className={`Contacts-photo${isMain ? ' Contacts-photo--main' : ''}`}>
              {src
                ? <img className="Contacts-photo-img" src={src} alt="" loading="lazy"
                    onClick={() => { if (p?.fullUrl) onLightbox(p.fullUrl); }} />
                : <span className="Contacts-photo-img Contacts-photo-img--empty" aria-hidden="true">{initialsOf(person)}</span>}
              <figcaption className="Contacts-photo-cap">
                {isMain
                  ? <span className="Contacts-photo-badge">Main</span>
                  : canEdit && <button type="button" className="Contacts-mini" disabled={busy}
                      onClick={() => onAct(t => setPhotoRef(t, person, n))}>Make main</button>}
                {isMain && canEdit && (
                  <button type="button" className="Contacts-mini Contacts-mini--danger" disabled={busy}
                    onClick={() => setPendingClear('remove')}>Remove</button>
                )}
              </figcaption>
            </figure>
          );
        })}
        {canEdit && (
          <label className={`Contacts-btn Contacts-addphoto${busy ? ' Contacts-btn--off' : ''}`}>
            {busy ? 'Working…' : '+ Photo'}
            {/* accept= lets iPhone Safari hand over HEIC as JPEG; the bytes are still checked. */}
            <input type="file" accept="image/jpeg,image/png" hidden disabled={busy} onChange={pick} />
          </label>
        )}
      </div>

      {main && !hasMainFile && (
        <p className="Contacts-photo-note">
          Main photo file not found in the library (<span className="Contacts-mono">{main}</span>).
          {canEdit && (<>{' '}Add a photo, pick another, or{' '}
            <button type="button" className="Contacts-mini Contacts-mini--danger" disabled={busy}
              onClick={() => setPendingClear('clear')}>Clear</button></>)}
        </p>
      )}

      {pendingClear && (
        <div className="Contacts-confirm" role="alertdialog" aria-live="polite">
          <span className="Contacts-confirm-text">
            {pendingClear === 'clear'
              ? 'Clear the main photo? The phone copy loses its photo; nothing in the library is touched.'
              : 'Remove the main photo? It comes off the phone; the file stays in the library.'}
          </span>
          <span className="Contacts-confirm-actions">
            <button type="button" className="Contacts-btn Contacts-btn--danger" disabled={busy}
              onClick={() => { setPendingClear(null); onAct(t => setPhotoRef(t, person, null)); }}>{busy ? 'Working…' : pendingClear === 'clear' ? 'Clear' : 'Remove'}</button>
            <button type="button" className="Contacts-btn" disabled={busy}
              onClick={() => setPendingClear(null)}>Cancel</button>
          </span>
        </div>
      )}
    </div>
  );
}

// ── one directory row ────────────────────────────────────────────────────────

export function Row({ person, people, expanded, onToggle, onMutate, busyId, photoMap, onLightbox, syncState, onResync, onResolved, onDelete, onOpenPerson, getIdToken, canEdit }) {
  const [mode, setMode] = useState(null);            // null | 'person' | 'channel' | 'newchannel'
  const [target, setTarget] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [err, setErr] = useState(null);
  const [photosStale, setPhotosStale] = useState(false);   // thumbnail map could not be rebuilt after a photo action
  const [panel, setPanel] = useState(null);                // resolvePanel.js state, or null
  const busy = busyId === person.personId;

  useEffect(() => {
    if (!expanded) { setMode(null); setErr(null); setPendingDelete(null); setPhotosStale(false); }
  }, [expanded]);

  // ── held / deleted pill -> the resolve panel ──
  const openPanel = async () => {
    const kind = syncState?.kind;
    const p = panelOpen(kind);
    if (!p) return;
    setPanel(p);
    if (kind !== 'held') return;
    try {
      const r = await fetchHeld(getIdToken, person.personId);
      setPanel(cur => (cur && cur.kind === 'held' ? panelLoaded(cur, r.fields) : cur));
    } catch (e) {
      setPanel(cur => panelFailed(cur, e.message || 'Could not compare with the phone'));
    }
  };
  const choose = async (choice) => {
    setPanel(cur => panelBusy(cur, choice));
    try {
      const r = await resolvePerson(getIdToken, person.personId, choice);
      setPanel(panelDone());
      // Re-read the row (new ExchangeId / fields / state) and set the pill from
      // what the server says the row holds now.
      await onResolved(person, stateFromResolve(r));
    } catch (e) {
      setPanel(cur => panelFailed(cur, resolveErrorText(e.status, e.body)));
    }
  };

  const run = async (fn, opts) => {
    setErr(null);
    setPhotosStale(false);
    try {
      await onMutate(person, fn, opts ? { ...opts, onPhotosStale: () => setPhotosStale(true) } : opts);
      setMode(null);
      setTarget(null);
    } catch (e) {
      // Message only — never the payload, which is contact data.
      setErr(e.message || 'Save failed');
    }
  };

  const chans = [...person.phones, ...person.emails];
  const editing = mode !== null;
  // Absorbed into another person (people-merge.js). Hidden from the list by
  // default; when opened, the detail links the survivor. Derived from the
  // column, not from syncState, so the link stays even if a reconcile ran.
  const merged = person.exchangeSyncState === 'merged';
  const survivor = merged && person.mergedInto ? people.find(p => p.personId === person.mergedInto) : null;
  // A merged row is frozen: no Edit, no Delete, no channel or photo controls —
  // the pill and the survivor link only. Edits belong on the survivor, and a
  // reconcile of an absorbed row is refused server-side anyway (merged gate).
  const rowCanEdit = canEdit && !merged;

  return (
    <li className={`Contacts-row${expanded ? ' Contacts-row--open' : ''}`}>
      <button type="button" className="Contacts-row-head" onClick={onToggle} aria-expanded={expanded}>
        <Avatar person={person} photo={photoMap.get(person.photoRef)} size="sm" />
        <span className="Contacts-row-main">
          <span className="Contacts-name">{person.title}</span>
          {person.org ? <span className="Contacts-org">{person.org}</span> : null}
          <SyncPill state={syncState} onRetry={rowCanEdit ? onResync : undefined} onResolve={rowCanEdit ? openPanel : undefined} />
        </span>
        <span className="Contacts-row-chevron" aria-hidden="true">{expanded ? '⌄' : '›'}</span>
      </button>

      <ResolvePanel panel={panel} onChoose={choose} onClose={() => setPanel(null)} />

      {chans.length > 0 && (
        <div className="Contacts-chans">
          {chans.map(c => (
            <ChannelRow key={c.id} chan={c} editing={expanded} canEdit={rowCanEdit} busy={busy}
              onStar={ch => run(t => (ch.isPrimary
                ? clearPrimary(t, person, ch)
                : setPrimary(t, person, ch)))}
              onEdit={ch => { setTarget(ch); setMode('channel'); setErr(null); }}
              onDelete={ch => { setPendingDelete(ch); setErr(null); }} />
          ))}

          {pendingDelete && (
            <div className="Contacts-confirm" role="alertdialog" aria-live="polite">
              <span className="Contacts-confirm-text">
                Delete {displayValue(pendingDelete)}?
              </span>
              <span className="Contacts-confirm-actions">
                <button type="button" className="Contacts-btn Contacts-btn--danger" disabled={busy}
                  onClick={e => {
                    e.stopPropagation();
                    const ch = pendingDelete;
                    setPendingDelete(null);
                    run(t => deleteChannel(t, person, ch));
                  }}>{busy ? 'Deleting…' : 'Delete'}</button>
                <button type="button" className="Contacts-btn" disabled={busy}
                  onClick={e => { e.stopPropagation(); setPendingDelete(null); }}>Cancel</button>
              </span>
            </div>
          )}
        </div>
      )}

      {expanded && (
        <div className="Contacts-detail-wrap">
          {err && <p className="Contacts-inline-error">{err}</p>}
          {photosStale && <p className="Contacts-photo-note">Couldn't refresh photos — reload the page to see the latest.</p>}
          {pillFor(syncState) && (
            <p className="Contacts-sync-line"><SyncPill state={syncState} onRetry={rowCanEdit ? onResync : undefined} onResolve={rowCanEdit ? openPanel : undefined} /></p>
          )}
          {merged && (
            <p className="Contacts-merged-line">
              {person.mergedInto
                ? (survivor
                    ? <>Merged into{' '}
                        <button type="button" className="Contacts-merged-link Contacts-mono"
                          onClick={e => { e.stopPropagation(); onOpenPerson(survivor); }}>{person.mergedInto}</button></>
                    : <>Merged into <span className="Contacts-mono">{person.mergedInto}</span> (not in the directory)</>)
                : 'Merged into another contact (survivor not recorded)'}
            </p>
          )}

          {mode === 'person' && (
            <PersonForm person={person} busy={busy}
              onCancel={() => setMode(null)}
              onSave={f => run(t => savePerson(t, person, f))} />
          )}

          {(mode === 'channel' || mode === 'newchannel') && (
            <ChannelForm people={people} person={person}
              channel={mode === 'channel' ? target : null} busy={busy}
              onCancel={() => { setMode(null); setTarget(null); }}
              onSave={({ kind, value, label }) => run(t => (mode === 'channel'
                ? saveChannel(t, person, target, { Value: value, Label: label })
                : addChannel(t, person, { kind, value, label })))} />
          )}

          {!editing && (
            <>
              {/* A row without a PersonId cannot own a photo (the + New card has none until saved). */}
              {person.personId && (
                <PhotoSection person={person} photoMap={photoMap} busy={busy} canEdit={rowCanEdit}
                  onLightbox={onLightbox} onAct={fn => run(fn, { photos: true })} />
              )}
              <dl className="Contacts-detail">
                {person.jobTitle ? (<><dt>Job title</dt><dd>{person.jobTitle}</dd></>) : null}
                {person.relationship ? (<><dt>Relationship</dt><dd>{person.relationship}</dd></>) : null}
                {person.birthday ? (<><dt>Birthday</dt><dd>{formatBirthday(person.birthday)}</dd></>) : null}
                {person.context ? (<><dt>Context</dt><dd className="Contacts-notes">{person.context}</dd></>) : null}
                {person.notes ? (<><dt>Notes</dt><dd className="Contacts-notes">{person.notes}</dd></>) : null}
                {person.tags ? (<><dt>Tags</dt><dd>{person.tags}</dd></>) : null}
                {person.domain ? (<><dt>Domain</dt><dd>{person.domain}</dd></>) : null}
                {person.standingSender ? (<><dt>Watch</dt><dd>Always recognize</dd></>) : null}
                {person.kind ? (<><dt>Kind</dt><dd>{person.kind}</dd></>) : null}
                {person.source ? (<><dt>Source</dt><dd>{person.source}</dd></>) : null}
                {person.personId ? (<><dt>PersonId</dt><dd className="Contacts-mono">{person.personId}</dd></>) : null}
              </dl>
              {rowCanEdit && (
                <div className="Contacts-row-actions">
                  <button type="button" className="Contacts-btn" disabled={busy}
                    onClick={() => { setMode('person'); setErr(null); }}>Edit</button>
                  <button type="button" className="Contacts-btn" disabled={busy}
                    onClick={() => { setMode('newchannel'); setErr(null); }}>+ Phone / Email</button>
                </div>
              )}
              {/* Last thing in the detail: delete everywhere (app rows + phone copy; photos stay). */}
              {rowCanEdit && person.personId && (
                <DeletePerson person={person} disabled={busy} onDelete={onDelete} />
              )}
            </>
          )}
        </div>
      )}
    </li>
  );
}

// ── page ─────────────────────────────────────────────────────────────────────

export default function Contacts() {
  const { instance, accounts } = useMsal();
  // Owners edit; anyone else who reached this page is a read-only viewer
  // (config/ownerAccess.js). Every write control below checks canEdit.
  const canEdit = userIsOwner(accounts[0]);
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  // Rows with ExchangeSyncState = merged are absorbed duplicates; off by default.
  const [showMerged, setShowMerged] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [shown, setShown] = useState(PAGE);
  const [photoMap, setPhotoMap] = useState(() => new Map());
  const [lightbox, setLightbox] = useState(null);
  const [creating, setCreating] = useState(false);
  const [createErr, setCreateErr] = useState(null);
  // PersonId -> pill state for the PHONE copy (Sync A). Separate from Row.err.
  const [syncState, setSyncState] = useState({});
  const sentinel = useRef(null);

  const getToken = useCallback(async () => {
    const account = accounts[0];
    if (!account) throw new Error('Not signed in');
    const res = await instance.acquireTokenSilent({ ...graphRequest, account });
    return res.accessToken;
  }, [instance, accounts]);

  // ID token for the Netlify relay (not the Graph access token — same MSAL
  // instance and account, same pattern as Watch.js / jarvisApi). forceRefresh
  // covers a stale cached ID token on 401.
  const getIdToken = useCallback(async (forceRefresh) => {
    const account = accounts[0];
    if (!account) throw new Error('Not signed in');
    const res = await instance.acquireTokenSilent({ ...graphRequest, account, forceRefresh: !!forceRefresh });
    return res.idToken;
  }, [instance, accounts]);
  const getIdTokenRef = useRef(getIdToken);
  useEffect(() => { getIdTokenRef.current = getIdToken; }, [getIdToken]);

  // ── Sync A: phone copy ──
  // Fire-and-forget after every save. The save itself never awaits this and
  // never fails because of it; the outcome lands in syncState only. One in
  // flight per PersonId, coalesced (see reconcileQueue.js).
  const okTimers = useRef(new Map());
  const setSync = useCallback((personId, state) => {
    setSyncState(prev => {
      const next = { ...prev };
      if (state) next[personId] = state; else delete next[personId];
      return next;
    });
  }, []);
  const queue = useRef(null);
  if (!queue.current) {
    queue.current = createReconcileQueue(async (personId) => {
      const prevTimer = okTimers.current.get(personId);
      if (prevTimer) { clearTimeout(prevTimer); okTimers.current.delete(personId); }
      setSync(personId, { kind: 'pending' });
      let state;
      try {
        state = stateFromResult(await reconcilePerson(getIdTokenRef.current, personId));
      } catch (e) {
        // Message only, and only to the console — never the payload.
        console.warn('[Contacts] reconcile failed:', e?.message || e);
        state = { kind: 'failed' };
      }
      setSync(personId, state);
      if (state.kind === 'ok') {
        okTimers.current.set(personId, setTimeout(() => { okTimers.current.delete(personId); setSync(personId, null); }, 3000));
      }
    });
  }
  const resync = useCallback((personId) => { queue.current.request(personId); }, []);
  useEffect(() => () => { for (const t of okTimers.current.values()) clearTimeout(t); }, []);

  const fetchData = useCallback(async () => {
    try {
      setError(null);
      setLoading(true);
      const token = await getToken();
      const dir = await loadDirectory(token);
      setPeople(dir);
      // Rows the last LIVE pass left held or stopped show their pill from the
      // column; everything else starts clean. Results from this session win
      // over the column once a reconcile has run.
      setSyncState(prev => {
        const next = {};
        for (const p of dir) {
          const fromCol = stateFromColumn(p.exchangeSyncState);
          if (prev[p.personId]) next[p.personId] = prev[p.personId];
          else if (fromCol) next[p.personId] = fromCol;
        }
        return next;
      });
      // Photos are decorative and resolved separately: loadPhotoMap never
      // throws, so a missing or unreadable library leaves the page working
      // with initials and no error banner.
      setPhotoMap(await loadPhotoMap(token));
    } catch (err) {
      setError(err.message || 'Failed to load contacts');
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const mergedCount = useMemo(() => people.filter(p => p.exchangeSyncState === 'merged').length, [people]);
  const filtered = useMemo(
    () => people.filter(p => (showMerged || p.exchangeSyncState !== 'merged') && matchesQuery(p, query)),
    [people, query, showMerged]);

  useEffect(() => { setShown(PAGE); }, [query, showMerged]);

  // "Merged into P-xxxxx" -> the survivor: search by its whole PersonId (which
  // matchesPersonId honours) so it is the one row on screen, and open it. The
  // merged toggle is left alone — the survivor is never merged itself.
  const openPerson = useCallback((target) => {
    setQuery(target.personId);
    setOpenId(target.id);
  }, []);

  // Incremental rendering: reveal another chunk when the sentinel scrolls in.
  useEffect(() => {
    const el = sentinel.current;
    if (!el || shown >= filtered.length) return undefined;
    const io = new IntersectionObserver(entries => {
      if (entries.some(e => e.isIntersecting)) setShown(s => s + PAGE);
    }, { rootMargin: '400px' });
    io.observe(el);
    return () => io.disconnect();
  }, [shown, filtered.length]);

  /**
   * Run one mutation and splice the RE-READ result back into the list.
   * The returned person is what SharePoint holds, not what the form held.
   */
  const mutate = useCallback(async (person, fn, opts = {}) => {
    setBusyId(person.personId);
    try {
      const token = await getToken();
      let fresh;
      try {
        fresh = await fn(token);
      } finally {
        // A photo action changed the library: rebuild the thumbnail map (its
        // URLs expire within the hour) — even when the action failed part-way,
        // since an uploaded file may now exist without being main. A failed
        // rebuild must not mask the action's own result or error: it is
        // reported through opts.onPhotosStale and nothing else.
        if (opts.photos) {
          try { setPhotoMap(await loadPhotoMap(token)); }
          catch (e) { console.warn('[Contacts] photo refresh failed:', e?.message || e); if (opts.onPhotosStale) opts.onPhotosStale(); }
        }
      }
      if (fresh) {
        setPeople(prev => prev.map(p => (p.personId === fresh.personId ? fresh : p)));
      } else {
        await fetchData();          // person vanished — fall back to a full reload
      }
    } finally {
      setBusyId(null);
      // Phone copy: fire-and-forget, and ALWAYS — including when the save threw.
      // setPrimary is two PATCHes (clear old, set new); if the second fails the
      // row has changed in SharePoint and the phone must still follow it. The
      // server reads SharePoint itself, so a reconcile after a failed save is at
      // worst a noop.
      if (person?.personId) queue.current.request(person.personId);
    }
  }, [getToken, fetchData]);

  // After a resolve: the server has already settled the phone copy, so re-read
  // the row (fields, ExchangeId, state) WITHOUT queueing another reconcile, and
  // set the pill from the resolve result.
  const resolved = useCallback(async (person, pillState) => {
    setBusyId(person.personId);
    try {
      const fresh = await reloadPerson(await getToken(), person.personId);
      if (fresh) setPeople(prev => prev.map(p => (p.personId === fresh.personId ? fresh : p)));
      else await fetchData();
      setSync(person.personId, pillState.kind === 'ok' ? null : pillState);
    } finally {
      setBusyId(null);
    }
  }, [getToken, fetchData, setSync]);

  // Delete person everywhere: the relay removes the phone copy, the channel
  // rows and the People row; then the list drops the row and the detail
  // closes. A 404 means the row was already gone — same outcome for the list.
  // On any other error the row stays and DeletePerson shows the message.
  const remove = useCallback(async (person) => {
    setBusyId(person.personId);
    try {
      try { await deletePerson(getIdTokenRef.current, person.personId); }
      catch (e) { if (e?.status !== 404) throw e; }
      setPeople(prev => removePerson(prev, person.personId));
      setSync(person.personId, null);
      setOpenId(null);
    } finally {
      setBusyId(null);
    }
  }, [setSync]);

  const handleCreate = async (f) => {
    setCreateErr(null);
    setBusyId('__new__');
    try {
      const fresh = await createPerson(await getToken(), f, people);
      if (fresh) {
        setPeople(prev => [...prev, fresh].sort((a, b) =>
          a.title.localeCompare(b.title, undefined, { sensitivity: 'base' }) ||
          a.personId.localeCompare(b.personId)));
        setOpenId(fresh.id);
        queue.current.request(fresh.personId);   // phone copy: create branch, fire-and-forget
      }
      setCreating(false);
    } catch (e) {
      setCreateErr(e.message || 'Could not create contact');
    } finally {
      setBusyId(null);
    }
  };

  // The headline count is the directory as shown: merged rows are excluded
  // unless the toggle is on, so the number matches what a scroll reveals.
  const total = showMerged ? people.length : people.length - mergedCount;
  const countLabel = loading
    ? 'Loading…'
    : query.trim()
      ? `${filtered.length} of ${total} ${total === 1 ? 'person' : 'people'}`
      : `${total} ${total === 1 ? 'person' : 'people'}`;

  const visible = filtered.slice(0, shown);

  return (
    <div className="Contacts">
      <header className="Contacts-header">
        <h1 className="Contacts-title">Contacts</h1>
        <span className="Contacts-count">{countLabel}</span>
        {canEdit && (
          <button type="button" className="Contacts-btn Contacts-btn--primary Contacts-new"
            onClick={() => { setCreating(true); setCreateErr(null); }}>+ New</button>
        )}
      </header>

      <input className="Contacts-search" type="search"
        placeholder="Search name, company, phone, email, notes, tags"
        value={query} onChange={e => setQuery(e.target.value)} autoComplete="off" />

      {mergedCount > 0 && (
        <div className="Contacts-filters">
          <label className="Contacts-toggle">
            <input type="checkbox" checked={showMerged} onChange={e => setShowMerged(e.target.checked)} />
            <span>Show merged</span>
          </label>
          <span>{mergedCount} merged {mergedCount === 1 ? 'contact' : 'contacts'} {showMerged ? 'shown' : 'hidden'}</span>
        </div>
      )}

      {creating && (
        <div className="Contacts-row Contacts-row--open Contacts-newcard">
          <p className="Contacts-newcard-head">
            New contact — will be saved as <span className="Contacts-mono">{nextPersonId(people)}</span>
          </p>
          {createErr && <p className="Contacts-inline-error">{createErr}</p>}
          <PersonForm person={null} busy={busyId === '__new__'}
            onCancel={() => setCreating(false)} onSave={handleCreate} />
        </div>
      )}

      {error && (
        <div className="Contacts-error">
          {error}
          <button type="button" className="Contacts-retry" onClick={fetchData}>Retry</button>
        </div>
      )}

      {!loading && !error && filtered.length === 0 && (
        <p className="Contacts-empty">{people.length === 0 ? 'No contacts yet.' : 'No matches.'}</p>
      )}

      <ul className="Contacts-list">
        {visible.map(p => (
          <Row key={p.id} person={p} people={people}
            expanded={openId === p.id}
            onToggle={() => setOpenId(openId === p.id ? null : p.id)}
            onMutate={mutate} busyId={busyId}
            photoMap={photoMap} onLightbox={setLightbox}
            syncState={syncState[p.personId] || null} onResync={() => resync(p.personId)}
            onResolved={resolved} onDelete={remove} onOpenPerson={openPerson} getIdToken={getIdTokenRef.current} canEdit={canEdit} />
        ))}
      </ul>

      {lightbox && (
        <div className="Contacts-lightbox" role="presentation" onClick={() => setLightbox(null)}>
          <img src={lightbox} alt="" />
        </div>
      )}

      {shown < filtered.length && (
        <div ref={sentinel} className="Contacts-more">
          Showing {visible.length} of {filtered.length}…
        </div>
      )}
    </div>
  );
}
